import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { inr, inrFull, fmtDate } from "@/utils";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Receipt, FileSignature, Landmark, Home, User as UserIcon,
  Plus, ArrowRight, IndianRupee, TrendingUp, ShieldCheck, Trash2, Pencil,
  Folder, FileText, Search, UploadCloud, Download,
} from "lucide-react";

const RECEIPT_TYPES = [
  { v: "booking", l: "Booking Amount" },
  { v: "installment", l: "Installment" },
  { v: "loan_disbursement", l: "Loan Disbursement (Bank)" },
  { v: "sale_deed_final", l: "Sale Deed Final Payment" },
];
const PAY_MODES = [
  { v: "cheque", l: "Cheque" }, { v: "bank_transfer", l: "Bank Transfer" }, { v: "upi", l: "UPI" },
  { v: "rtgs", l: "RTGS/NEFT" }, { v: "cash", l: "Cash" }, { v: "credit_card", l: "Card" },
];

function TimelineIcon({ type }) {
  const map = {
    customer_created: UserIcon, price_negotiated: TrendingUp, booking: Receipt,
    receipt_installment: IndianRupee, receipt_booking: Receipt, receipt_loan_disbursement: Landmark,
    receipt_sale_deed_final: FileSignature, loan_sanction: Landmark, agreement: FileSignature,
    sale_deed: ShieldCheck, possession: Home,
  };
  const Icon = map[type] || Circle;
  return <Icon size={14} />;
}

export default function SalesWorkflow() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [openCustomerId, setOpenCustomerId] = useState(null);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers-wf", projectId],
    queryFn: async () => (await api.get("/customers", { params: projectId ? { project_id: projectId } : {} })).data,
  });
  const { data: finSettings } = useQuery({
    queryKey: ["finance-settings"],
    queryFn: async () => (await api.get("/finance/settings")).data,
  });

  const defaultGst = finSettings?.default_gst_rate ?? 5;
  const defaultTds = finSettings?.default_tds_rate ?? 1;

  const activeCustomers = useMemo(
    () => customers.filter((c) => c.active !== false),
    [customers]
  );

  return (
    <div className="space-y-6" data-testid="sales-workflow-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Sales · Revenue Recognition</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Sales Workflow</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Booking → Installments → Loan → Sale Deed. Collections sit as <b>Customer Advances</b> until the Sale Deed is registered — then automatically become <b>Recognized Revenue</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TdsGstSettingsButton finSettings={finSettings} />
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="wf-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Configured GST</div>
          <div className="text-xl font-bold font-mono mt-1">{defaultGst}%</div>
        </CardContent></Card>
        <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Configured TDS (194-IA)</div>
          <div className="text-xl font-bold font-mono mt-1">{defaultTds}%</div>
        </CardContent></Card>
        <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Customers</div>
          <div className="text-xl font-bold font-mono mt-1">{customers.length}</div>
        </CardContent></Card>
        <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sale Deeds Done</div>
          <div className="text-xl font-bold font-mono mt-1">{customers.filter((c) => c.sale_deed_completed).length}</div>
        </CardContent></Card>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Final Price</TableHead>
              <TableHead className="text-right">Collected</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {activeCustomers.map((c) => {
                const total = Number(c.total_amount || 0);
                const paid = Number(c.paid_amount || 0);
                const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
                const remaining = Math.max(total - paid, 0);
                const isRec = c.sale_deed_completed;
                return (
                  <TableRow key={c.id} className="data-row" data-testid={`wf-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] text-zinc-500">{c.phone}</div>
                    </TableCell>
                    <TableCell><span className="text-[10px] uppercase tracking-widest bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{c.status || "inquiry"}</span></TableCell>
                    <TableCell className="text-right font-mono">{inr(total)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{inr(paid)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{inr(remaining)}</TableCell>
                    <TableCell>
                      <div className="w-32 h-2 bg-zinc-200 rounded overflow-hidden">
                        <div className={`h-full ${isRec ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{pct}%</div>
                    </TableCell>
                    <TableCell>
                      {isRec
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-100 border border-emerald-300 text-emerald-800 px-1.5 py-0.5 rounded"><ShieldCheck size={11} /> Recognized</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded">Advances (Liability)</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setOpenCustomerId(c.id)} data-testid={`open-wf-${c.id}`}>
                        Open <ArrowRight size={12} className="ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!customers.length && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-16">
                  No customers yet. Add one from the Customers page to start the sales workflow.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {openCustomerId && (
        <CustomerWorkflowDialog
          customerId={openCustomerId}
          onClose={() => { setOpenCustomerId(null); qc.invalidateQueries({ queryKey: ["customers-wf"] }); }}
          defaultGst={defaultGst}
          defaultTds={defaultTds}
        />
      )}
    </div>
  );
}


// ============ Customer Workflow Dialog ============
function CustomerWorkflowDialog({ customerId, onClose, defaultGst, defaultTds }) {
  const qc = useQueryClient();
  const { data: timeline, refetch } = useQuery({
    queryKey: ["timeline", customerId],
    queryFn: async () => (await api.get(`/customers/${customerId}/timeline`)).data,
    enabled: !!customerId,
  });
  const [rcptOpen, setRcptOpen] = useState(false);
  const [sdOpen, setSdOpen] = useState(false);

  const [rcpt, setRcpt] = useState({
    receipt_type: "installment", source: "Installment Collection", gross_amount: 0,
    date: new Date().toISOString().slice(0, 10), payment_mode: "bank_transfer",
    reference_number: "", gst_rate: 0, tds_rate: 0, description: "",
  });

  const [sd, setSd] = useState({
    sale_deed_number: "", registration_number: "", registration_date: new Date().toISOString().slice(0, 10),
    stamp_duty: 0, registration_charges: 0, final_gst: 0, tds_deducted: 0, notes: "",
  });

  // Live TDS/GST calc on receipt
  const rcptCalc = useMemo(() => {
    const g = Number(rcpt.gross_amount) || 0;
    const gst = +(g * (Number(rcpt.gst_rate) || 0) / 100).toFixed(2);
    const tds = +(g * (Number(rcpt.tds_rate) || 0) / 100).toFixed(2);
    return { gst, tds, net: +(g - tds).toFixed(2) };
  }, [rcpt.gross_amount, rcpt.gst_rate, rcpt.tds_rate]);

  async function saveReceipt() {
    if (!rcpt.gross_amount || +rcpt.gross_amount <= 0) return toast.error("Enter gross amount");
    try {
      const payload = {
        project_id: timeline.customer.project_id,
        customer_id: customerId,
        unit_id: timeline.customer.unit_id || null,
        source: rcpt.source || (RECEIPT_TYPES.find((r) => r.v === rcpt.receipt_type)?.l) || "Receipt",
        receipt_type: rcpt.receipt_type,
        gross_amount: +rcpt.gross_amount,
        amount: +rcpt.gross_amount, // will be recomputed server-side (gross - tds)
        gst_rate: +rcpt.gst_rate,
        tds_rate: +rcpt.tds_rate,
        date: rcpt.date, payment_mode: rcpt.payment_mode,
        reference_number: rcpt.reference_number,
        description: rcpt.description,
      };
      await api.post("/incomes", payload);
      toast.success("Receipt recorded · Customer ledger updated");
      qc.invalidateQueries({ queryKey: ["timeline", customerId] });
      qc.invalidateQueries({ queryKey: ["customers-wf"] });
      qc.invalidateQueries({ queryKey: ["pnl-page"] });
      qc.invalidateQueries({ queryKey: ["dashboard-kpi"] });
      setRcptOpen(false);
      setRcpt({ ...rcpt, gross_amount: 0, reference_number: "", description: "" });
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function saveSaleDeed() {
    if (!sd.sale_deed_number) return toast.error("Sale Deed Number required");
    try {
      const payload = {
        ...sd,
        customer_id: customerId,
        project_id: timeline.customer.project_id,
        unit_id: timeline.customer.unit_id || null,
        stamp_duty: +sd.stamp_duty || 0,
        registration_charges: +sd.registration_charges || 0,
        final_gst: +sd.final_gst || 0,
        tds_deducted: +sd.tds_deducted || 0,
      };
      await api.post("/sale-deeds", payload);
      toast.success("Sale Deed registered · Revenue recognized ✓");
      // Exhaustive invalidation so every place that shows revenue refreshes
      ["timeline", "customers-wf", "pnl-page", "dashboard-kpi", "dashboard-units",
       "incomes", "receipts", "units", "expense-summary", "sale-deeds",
      ].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setSdOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function delReceipt(rid) {
    if (!window.confirm("Delete this receipt? Customer paid amount will be recalculated.")) return;
    try {
      await api.delete(`/incomes/${rid}`);
      toast.success("Receipt deleted");
      qc.invalidateQueries({ queryKey: ["timeline", customerId] });
      qc.invalidateQueries({ queryKey: ["customers-wf"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function deleteSaleDeed(sdId) {
    if (!window.confirm("Delete this Sale Deed?\n\nThis will REVERSE revenue recognition — receipts will fall back to Customer Advances.")) return;
    try {
      await api.delete(`/sale-deeds/${sdId}`);
      toast.success("Sale Deed deleted · Revenue reverted to advances");
      qc.invalidateQueries({ queryKey: ["timeline", customerId] });
      qc.invalidateQueries({ queryKey: ["customers-wf"] });
      qc.invalidateQueries({ queryKey: ["pnl-page"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  if (!timeline) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent><div className="text-sm text-zinc-500 py-8 text-center">Loading…</div></DialogContent>
      </Dialog>
    );
  }

  const s = timeline.summary;
  const c = timeline.customer;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="wf-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{c.name}
            {timeline.sale_deed && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-100 border border-emerald-300 text-emerald-800 px-1.5 py-0.5 rounded"><ShieldCheck size={11} /> Revenue Recognized</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Final Price</div>
            <div className="text-lg font-bold font-mono mt-1">{inrFull(s.final_price)}</div>
          </CardContent></Card>
          <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Collected (Net)</div>
            <div className="text-lg font-bold font-mono mt-1 text-emerald-700">{inrFull(s.total_received)}</div>
          </CardContent></Card>
          <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">GST Collected</div>
            <div className="text-lg font-bold font-mono mt-1">{inrFull(s.total_gst)}</div>
          </CardContent></Card>
          <Card className="border-zinc-200 bg-white"><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">TDS Deducted</div>
            <div className="text-lg font-bold font-mono mt-1 text-amber-700">{inrFull(s.total_tds)}</div>
          </CardContent></Card>
          <Card className={`border-zinc-200 ${s.revenue_recognized ? "bg-emerald-50" : "bg-white"}`}><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Remaining</div>
            <div className="text-lg font-bold font-mono mt-1 text-red-600">{inrFull(s.remaining)}</div>
          </CardContent></Card>
        </div>

        <div className="flex gap-2 mt-3">
          <Dialog open={rcptOpen} onOpenChange={setRcptOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-receipt-btn"><Plus size={14} className="mr-1" /> Add Receipt</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-heading">Record Payment Receipt</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Receipt Type</Label>
                  <Select value={rcpt.receipt_type} onValueChange={(v) => setRcpt({ ...rcpt, receipt_type: v, source: RECEIPT_TYPES.find((r) => r.v === v)?.l || "" })}>
                    <SelectTrigger data-testid="rcpt-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{RECEIPT_TYPES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Gross Amount (₹)</Label><Input type="number" value={rcpt.gross_amount} onChange={(e) => setRcpt({ ...rcpt, gross_amount: e.target.value })} data-testid="rcpt-gross" /></div>
                <div><Label>Date</Label><Input type="date" value={rcpt.date} onChange={(e) => setRcpt({ ...rcpt, date: e.target.value })} /></div>
                <div><Label>Payment Mode</Label>
                  <Select value={rcpt.payment_mode} onValueChange={(v) => setRcpt({ ...rcpt, payment_mode: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Reference #</Label><Input value={rcpt.reference_number} onChange={(e) => setRcpt({ ...rcpt, reference_number: e.target.value })} placeholder="Cheque / UPI / NEFT ref" data-testid="rcpt-ref" /></div>
                <div><Label>GST Rate (%) <span className="text-[10px] text-zinc-500 font-normal">Optional</span></Label><Input type="number" step="0.01" placeholder="0" value={rcpt.gst_rate} onChange={(e) => setRcpt({ ...rcpt, gst_rate: e.target.value })} data-testid="rcpt-gst-rate" /></div>
                <div><Label>TDS Rate (%) <span className="text-[10px] text-zinc-500 font-normal">Optional</span></Label><Input type="number" step="0.01" placeholder="0" value={rcpt.tds_rate} onChange={(e) => setRcpt({ ...rcpt, tds_rate: e.target.value })} data-testid="rcpt-tds-rate" /></div>
                <div className="col-span-2"><Label>Description</Label><Input value={rcpt.description} onChange={(e) => setRcpt({ ...rcpt, description: e.target.value })} /></div>

                <div className="col-span-2 bg-zinc-950 text-white rounded-md p-3 font-mono text-xs space-y-1">
                  <div className="flex justify-between"><span className="opacity-70">Gross Amount</span><span>{inrFull(+rcpt.gross_amount || 0)}</span></div>
                  <div className="flex justify-between"><span className="opacity-70">GST @ {rcpt.gst_rate}%</span><span>+ {inrFull(rcptCalc.gst)}</span></div>
                  <div className="flex justify-between text-amber-300"><span className="opacity-70">TDS @ {rcpt.tds_rate}%</span><span>− {inrFull(rcptCalc.tds)}</span></div>
                  <div className="flex justify-between border-t border-white/20 pt-2 mt-1 text-emerald-300 font-semibold text-sm"><span>Net Received</span><span data-testid="rcpt-net">{inrFull(rcptCalc.net)}</span></div>
                </div>
              </div>
              <DialogFooter><Button onClick={saveReceipt} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-receipt">Record Receipt</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {!timeline.sale_deed && s.total_received > 0 && (
            <Dialog open={sdOpen} onOpenChange={setSdOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-700 hover:bg-purple-800" data-testid="register-sd-btn">
                  <FileSignature size={14} className="mr-1" /> Register Sale Deed
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="font-heading">Register Sale Deed · Recognize Revenue</DialogTitle></DialogHeader>
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-xs mb-2">
                  ⚠️ Registering the Sale Deed will move <b>{inrFull(s.total_received)}</b> from <b>Customer Advances</b> (liability) to <b>Recognized Revenue</b> in P&L. This is a critical accounting event.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Sale Deed Number *</Label><Input value={sd.sale_deed_number} onChange={(e) => setSd({ ...sd, sale_deed_number: e.target.value })} data-testid="sd-number" /></div>
                  <div><Label>Registration Number</Label><Input value={sd.registration_number} onChange={(e) => setSd({ ...sd, registration_number: e.target.value })} /></div>
                  <div><Label>Registration Date</Label><Input type="date" value={sd.registration_date} onChange={(e) => setSd({ ...sd, registration_date: e.target.value })} /></div>
                  <div><Label>Stamp Duty (₹)</Label><Input type="number" value={sd.stamp_duty} onChange={(e) => setSd({ ...sd, stamp_duty: e.target.value })} /></div>
                  <div><Label>Registration Charges (₹)</Label><Input type="number" value={sd.registration_charges} onChange={(e) => setSd({ ...sd, registration_charges: e.target.value })} /></div>
                  <div><Label>Final GST Paid (₹)</Label><Input type="number" value={sd.final_gst} onChange={(e) => setSd({ ...sd, final_gst: e.target.value })} /></div>
                  <div><Label>TDS Deducted (₹)</Label><Input type="number" value={sd.tds_deducted} onChange={(e) => setSd({ ...sd, tds_deducted: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Notes</Label><Input value={sd.notes} onChange={(e) => setSd({ ...sd, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveSaleDeed} className="bg-purple-700 hover:bg-purple-800" data-testid="save-sd">Register &amp; Recognize Revenue</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {timeline.sale_deed && (
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteSaleDeed(timeline.sale_deed.id)} data-testid="del-sd-btn">
              <Trash2 size={13} className="mr-1" /> Delete Sale Deed
            </Button>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline</div>
          <div className="relative pl-6 border-l-2 border-zinc-200 space-y-3">
            {timeline.events.map((ev, i) => (
              <div key={`${ev.type}-${ev.date}-${i}`} className="relative">
                <div className="absolute -left-[27px] top-1 w-4 h-4 rounded-full bg-white border-2 border-emerald-500 grid place-items-center">
                  <TimelineIcon type={ev.type} />
                </div>
                <div className="bg-white border border-zinc-200 rounded-md p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold">{ev.title}</div>
                      {ev.description && <div className="text-xs text-zinc-500">{ev.description}</div>}
                    </div>
                    <div className="text-right">
                      {ev.amount > 0 && <div className="font-mono text-sm">{inr(ev.amount)}</div>}
                      <div className="text-[10px] text-zinc-500">{fmtDate(ev.date)}</div>
                      {ev.revenue_recognized === false && ev.type.startsWith("receipt_") && (
                        <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1 rounded">advance</span>
                      )}
                      {ev.revenue_recognized === true && ev.type.startsWith("receipt_") && (
                        <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1 rounded">recognized</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Receipts detail table */}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">All Receipts</div>
          <ReceiptsTable customerId={customerId} onDelete={delReceipt} />
        </div>

        {/* Documents Folder */}
        <div className="mt-6">
          <CustomerDocuments customerId={customerId} projectId={c.project_id} />
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ Customer Documents Folder (Agreement / Registration / Loan / OCR search) ============
const DOC_FOLDERS = [
  { v: "booking", l: "Booking" },
  { v: "agreement", l: "Agreement" },
  { v: "sale_deed", l: "Sale Deed" },
  { v: "registration", l: "Registration" },
  { v: "loan", l: "Loan Receipts" },
  { v: "receipt", l: "Payment Receipts" },
  { v: "kyc", l: "KYC" },
  { v: "other", l: "Other" },
];

function CustomerDocuments({ customerId, projectId }) {
  const qc = useQueryClient();
  const [folder, setFolder] = useState("agreement");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: docs = [] } = useQuery({
    queryKey: ["customer-docs", customerId, folder],
    queryFn: async () => (await api.get("/documents", { params: { customer_id: customerId, category: folder } })).data,
    enabled: !!customerId,
  });

  const { data: allDocs = [] } = useQuery({
    queryKey: ["customer-docs-counts", customerId],
    queryFn: async () => (await api.get("/documents", { params: { customer_id: customerId } })).data,
    enabled: !!customerId,
  });

  const { data: searchResults = [], refetch: runSearch, isFetching: searching } = useQuery({
    queryKey: ["doc-search", customerId, q],
    queryFn: async () => (await api.get("/documents/search", { params: { q, customer_id: customerId } })).data,
    enabled: false,
  });

  const counts = useMemo(() => {
    const m = {};
    for (const d of allDocs) m[d.category] = (m[d.category] || 0) + 1;
    return m;
  }, [allDocs]);

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      fd.append("customer_id", customerId);
      fd.append("category", folder);
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}${data.has_ocr ? " · OCR indexed" : ""}`);
      qc.invalidateQueries({ queryKey: ["customer-docs", customerId] });
      qc.invalidateQueries({ queryKey: ["customer-docs-counts", customerId] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  async function delDoc(id) {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      qc.invalidateQueries({ queryKey: ["customer-docs", customerId] });
      qc.invalidateQueries({ queryKey: ["customer-docs-counts", customerId] });
      qc.invalidateQueries({ queryKey: ["doc-search", customerId] });
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  }

  function download(doc) {
    const token = localStorage.getItem("param_token");
    window.open(`${API}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  }

  return (
    <div data-testid="customer-docs">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Folder size={12} /> Documents · <span className="font-mono">{allDocs.length}</span> file{allDocs.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="OCR search inside PDFs & images…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) runSearch(); }}
            className="h-8 w-64"
            data-testid="doc-search-input"
          />
          <Button size="sm" variant="outline" disabled={!q.trim() || searching} onClick={() => runSearch()} data-testid="doc-search-btn">
            <Search size={12} className="mr-1" /> {searching ? "…" : "Search"}
          </Button>
        </div>
      </div>

      {/* Folder tabs */}
      <div className="flex flex-wrap gap-1 mb-3">
        {DOC_FOLDERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setFolder(f.v)}
            className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded border transition-colors ${
              folder === f.v
                ? "bg-emerald-700 border-emerald-700 text-white"
                : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid={`doc-folder-${f.v}`}
          >
            <Folder size={11} /> {f.l}
            <span className={`ml-1 font-mono text-[10px] ${folder === f.v ? "text-emerald-100" : "text-zinc-400"}`}>
              {counts[f.v] || 0}
            </span>
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-1 text-xs px-3 h-8 bg-emerald-700 hover:bg-emerald-800 text-white rounded cursor-pointer" data-testid="doc-upload-label">
          <UploadCloud size={13} /> {uploading ? "Uploading…" : `Upload to ${DOC_FOLDERS.find((f) => f.v === folder)?.l}`}
          <input type="file" hidden onChange={onUpload} disabled={uploading} data-testid="doc-upload-input" />
        </label>
      </div>

      {/* Search results override the folder view */}
      {q.trim() && searchResults.length > 0 ? (
        <div className="rounded border border-emerald-200 bg-emerald-50/40 divide-y" data-testid="doc-search-results">
          <div className="px-3 py-2 text-[11px] uppercase tracking-widest text-emerald-800">
            <b>{searchResults.length}</b> match{searchResults.length === 1 ? "" : "es"} for "{q}"
          </div>
          {searchResults.map((d) => (
            <div key={d.id} className="px-3 py-2 flex items-start gap-3">
              <FileText size={14} className="text-emerald-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{d.filename}</div>
                <div className="text-[11px] text-zinc-600 mt-0.5 line-clamp-2">{d.snippet || "match found in filename"}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{d.category} · {fmtDate(d.uploaded_at)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(d)}><Download size={12} /></Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>File</TableHead>
              <TableHead>OCR</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Size</TableHead>
              <TableHead className="w-[110px]" />
            </TableRow></TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} className="data-row" data-testid={`doc-row-${d.id}`}>
                  <TableCell className="flex items-center gap-2"><FileText size={13} className="text-emerald-700" /><span className="text-sm">{d.filename}</span></TableCell>
                  <TableCell>
                    {d.has_ocr
                      ? <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded">indexed</span>
                      : <span className="text-[9px] uppercase tracking-widest bg-zinc-100 text-zinc-500 border border-zinc-200 px-1.5 py-0.5 rounded">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">{d.uploaded_by_name}<br /><span className="text-zinc-500 font-mono">{fmtDate(d.uploaded_at)}</span></TableCell>
                  <TableCell className="text-right font-mono text-xs">{Math.round((d.size || 0) / 1024)} KB</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => download(d)} className="mr-1" data-testid={`doc-dl-${d.id}`}><Download size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => delDoc(d.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`doc-del-${d.id}`}><Trash2 size={12} /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!docs.length && (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-zinc-500 py-6">
                  No files in this folder yet — click <b>Upload</b> above to add {DOC_FOLDERS.find((f) => f.v === folder)?.l.toLowerCase()} documents.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


function ReceiptsTable({ customerId, onDelete }) {
  const { data: receipts = [] } = useQuery({
    queryKey: ["receipts", customerId],
    queryFn: async () => (await api.get("/incomes", { params: { customer_id: customerId } })).data,
    enabled: !!customerId,
  });
  return (
    <div className="border rounded overflow-hidden">
      <Table>
        <TableHeader className="bg-zinc-50"><TableRow>
          <TableHead>Receipt #</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead>
          <TableHead>Mode / Ref</TableHead>
          <TableHead className="text-right">Gross</TableHead>
          <TableHead className="text-right">GST</TableHead>
          <TableHead className="text-right">TDS</TableHead>
          <TableHead className="text-right">Net</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {receipts.map((r) => (
            <TableRow key={r.id} className="data-row" data-testid={`receipt-row-${r.id}`}>
              <TableCell className="font-mono text-xs font-semibold">{r.receipt_number}</TableCell>
              <TableCell className="text-xs font-mono">{fmtDate(r.date)}</TableCell>
              <TableCell className="text-xs">{r.receipt_type}</TableCell>
              <TableCell className="text-xs">{r.payment_mode?.replace("_", " ")}<br /><span className="text-zinc-500">{r.reference_number}</span></TableCell>
              <TableCell className="text-right font-mono">{inr(r.gross_amount || r.amount)}</TableCell>
              <TableCell className="text-right font-mono">{inr(r.gst_amount || 0)}</TableCell>
              <TableCell className="text-right font-mono text-amber-700">{inr(r.tds_amount || 0)}</TableCell>
              <TableCell className="text-right font-mono text-emerald-700">{inr(r.amount)}</TableCell>
              <TableCell className="text-center">
                {r.revenue_recognized
                  ? <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded"><CheckCircle2 size={9} className="inline" /> Revenue</span>
                  : <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Advance</span>}
              </TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={() => onDelete(r.id)} className="text-red-600" data-testid={`del-receipt-${r.id}`}><Trash2 size={12} /></Button></TableCell>
            </TableRow>
          ))}
          {!receipts.length && <TableRow><TableCell colSpan={10} className="text-center text-xs text-zinc-500 py-6">No receipts yet — click "Add Receipt" above to record the first collection.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}


function TdsGstSettingsButton({ finSettings }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [gst, setGst] = useState(finSettings?.default_gst_rate ?? 5);
  const [tds, setTds] = useState(finSettings?.default_tds_rate ?? 1);
  React.useEffect(() => {
    if (finSettings) {
      setGst(finSettings.default_gst_rate);
      setTds(finSettings.default_tds_rate);
    }
  }, [finSettings, setGst, setTds]);
  async function save() {
    try {
      await api.patch("/finance/settings/default_gst_rate", { value: +gst });
      await api.patch("/finance/settings/default_tds_rate", { value: +tds });
      toast.success("Rates updated");
      qc.invalidateQueries({ queryKey: ["finance-settings"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="wf-settings-btn">Configure GST / TDS Rates</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Default GST &amp; TDS Rates</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Default GST Rate (%)</Label>
            <Input type="number" step="0.01" value={gst} onChange={(e) => setGst(e.target.value)} data-testid="setting-gst" />
            <div className="text-[11px] text-zinc-500 mt-1">Applied to all customer receipts unless overridden per receipt.</div>
          </div>
          <div>
            <Label>Default TDS Rate (%) — 194-IA</Label>
            <Input type="number" step="0.01" value={tds} onChange={(e) => setTds(e.target.value)} data-testid="setting-tds" />
            <div className="text-[11px] text-zinc-500 mt-1">TDS deducted by buyer on property purchases above ₹50L (typically 1%).</div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-rates">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
